import React, { useState, useEffect } from 'react';
import styles from './Header.module.scss';
import { useCart } from '../../../context/CartContext'; 
import { IoCartOutline, IoPersonOutline, IoMenuOutline, IoCloseOutline, IoSearchOutline, IoChevronDownOutline, IoAddOutline, IoRemoveOutline, IoCallOutline, IoLocationOutline, IoCubeOutline } from 'react-icons/io5';
import { Link, NavLink, useLocation } from 'react-router-dom';
import adminService from '../../../services/adminService'; 

const Header = () => {
  const { getCartItemCount } = useCart();
  const itemCount = getCartItemCount();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  const [categories, setCategories] = useState([]);
  const [isMobileSubmenuOpen, setIsMobileSubmenuOpen] = useState(false);

  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // 1. Fetch Categories
  useEffect(() => {
    adminService.getCategories()
      .then(res => {
        if (Array.isArray(res.data)) setCategories(res.data);
      })
      .catch(err => console.error("Header Error:", err));
  }, []);

  // 2. Handle Scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 3. Reset state khi chuyển trang
  useEffect(() => {
    setIsMenuOpen(false);
    setIsMobileSubmenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <>
      {/* === TOP BAR === */}
      <div className={`
        ${styles.topBar} 
        ${isScrolled ? styles.hidden : ''} 
        ${isHomePage && !isScrolled ? styles.transparent : styles.dark}
      `}>
        <div className={styles.container}>
          <div className={styles.topBarLeft}>
            <a href="tel:0964472868" className={styles.topLink}>
              <IoCallOutline /> 0964.472.868
            </a>
            <span className={styles.divider}>|</span>
            <span><IoLocationOutline /> Hà Nội, Việt Nam</span>
          </div>
          <div className={styles.topBarRight}>
            <Link to="/gioi-thieu">Về Dũng Như</Link>
            <span className={styles.divider}>|</span>
            <Link to="/tra-cuu-don-hang"><IoCubeOutline /> Tra Cứu Đơn Hàng</Link>
          </div>
        </div>
      </div>

      {/* === MAIN HEADER === */}
      <header className={`
        ${styles.header} 
        ${isScrolled ? styles.sticky : ''}
        ${isHomePage && !isScrolled ? styles.transparent : styles.solid}
      `}>
        <div className={styles.container}>
          
          {/* Mobile Toggle (Trái) */}
          <div className={styles.mobileLeft}>
            <button className={styles.hamburgerButton} onClick={() => setIsMenuOpen(true)}>
              <IoMenuOutline />
            </button>
          </div>

          {/* Logo (Giữa trên Mobile) */}
          <div className={styles.logoWrapper}>
            <Link to="/" className={styles.logoLink}> 
              <img src="/logo.png" alt="Dũng Như - Trầm Hương" />
            </Link>
          </div>

          {/* Desktop Nav (Giữa trên PC) */}
          <nav className={styles.desktopNav}>
            <NavLink to="/" className={({isActive}) => isActive ? styles.activeLink : ''}>Trang Chủ</NavLink>
            
            <div className={styles.navItemWithSub}>
              <NavLink to="/san-pham" className={({isActive}) => isActive ? styles.activeLink : ''}>
                Sản Phẩm <IoChevronDownOutline className={styles.arrowIcon} />
              </NavLink>
              <div className={styles.dropdownMenu}>
                {categories.length > 0 ? (
                  categories.map(cat => (
                    <Link key={cat.id} to={`/san-pham?category_id=${cat.id}`} className={styles.dropdownLink}>
                      {cat.name}
                    </Link>
                  ))
                ) : (
                  <span className={styles.emptyMsg}>Đang cập nhật...</span>
                )}
              </div>
            </div>

            <NavLink to="/cau-chuyen" className={({isActive}) => isActive ? styles.activeLink : ''}>Câu Chuyện Trầm</NavLink>
            <NavLink to="/vung-nguyen-lieu" className={({isActive}) => isActive ? styles.activeLink : ''}>Vùng Nguyên Liệu</NavLink>
          </nav>

          {/* Actions (Phải) */}
          <div className={styles.headerActions}>
            <Link to="/tra-cuu-don-hang" className={`${styles.iconButton} ${styles.desktopOnly}`} title="Tìm kiếm">
               <IoSearchOutline/>
            </Link>
            <Link to="/admin/login" className={`${styles.iconButton} ${styles.desktopOnly}`} title="Tài khoản">
              <IoPersonOutline />
            </Link>
            
            <Link to="/gio-hang" className={styles.cartButtonWrapper} title="Giỏ hàng">
              <IoCartOutline className={styles.cartIcon} />
              {itemCount > 0 && <span className={styles.cartBadge}>{itemCount}</span>}
            </Link>
          </div>
        </div>
      </header>

      {/* Spacer */}
      {!isHomePage && <div className={styles.headerSpacer}></div>}

      {/* === MOBILE MENU DRAWER === */}
      <div className={`${styles.mobileMenuOverlay} ${isMenuOpen ? styles.open : ''}`} onClick={() => setIsMenuOpen(false)}></div>
      
      <div className={`${styles.mobileSideMenu} ${isMenuOpen ? styles.open : ''}`}>
        <div className={styles.mobileMenuHeader}>
          <Link to="/" className={styles.drawerLogo} onClick={() => setIsMenuOpen(false)}>
            <img src="/logo.png" alt="Dũng Như" />
          </Link>
          <button className={styles.closeMenuBtn} onClick={() => setIsMenuOpen(false)}>
            <IoCloseOutline />
          </button>
        </div>

        <div className={styles.mobileMenuContent}>
          <nav className={styles.mobileNavLinks}>
            <NavLink to="/">Trang Chủ</NavLink>
            
            <div className={styles.mobileNavItem}>
              <div className={styles.mobileNavRow}>
                <NavLink to="/san-pham" onClick={() => setIsMenuOpen(false)}>Sản Phẩm</NavLink>
                <button className={styles.toggleSubBtn} onClick={(e) => { e.preventDefault(); setIsMobileSubmenuOpen(!isMobileSubmenuOpen); }}>
                  {isMobileSubmenuOpen ? <IoRemoveOutline /> : <IoAddOutline />}
                </button>
              </div>
              <div className={`${styles.mobileSubmenu} ${isMobileSubmenuOpen ? styles.open : ''}`}>
                <Link to="/san-pham" onClick={() => setIsMenuOpen(false)} className={styles.subLink}>-- Tất cả sản phẩm --</Link>
                {categories.map(cat => (
                  <Link key={cat.id} to={`/san-pham?category_id=${cat.id}`} className={styles.subLink} onClick={() => setIsMenuOpen(false)}>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <NavLink to="/cau-chuyen" onClick={() => setIsMenuOpen(false)}>Câu Chuyện Trầm</NavLink>
            <NavLink to="/vung-nguyen-lieu" onClick={() => setIsMenuOpen(false)}>Vùng Nguyên Liệu</NavLink>
            
            <div className={styles.mobileDivider}></div>
            <Link to="/gioi-thieu" className={styles.mobileExtraLink} onClick={() => setIsMenuOpen(false)}>Về Dũng Như</Link>
            <Link to="/tra-cuu-don-hang" className={styles.mobileExtraLink} onClick={() => setIsMenuOpen(false)}>Tra cứu đơn hàng</Link>
          </nav>

          <div className={styles.mobileMenuFooter}>
            <Link to="/admin/login" className={styles.mobileFooterLink} onClick={() => setIsMenuOpen(false)}>
              <IoPersonOutline /> Đăng nhập / Tài khoản
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;